import {
  Box,
  Container,
  Grid,
  IconButton,
  Paper,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import BackButton from "components/BackButton";
import ButtonAction from "pages/Wallet/Component/ButtonAction";
import CoinSingleDrawer from "./Components/CoinSingleDrawer";
import { DefaultUrlWeb } from "config/Configuration";
import { Down, TooltipIcon } from "assets/Icon";
import { GetSpotWalletList } from "store/api/wallet";
import NumberFormats from "components/NumberFormats";
import { SpaceBetweenBox } from "components/GeneralStyled";
import http from "config/http";
import useNotify from "hooks/useNotify";

function SpotCoinSingle() {
  const theme = useTheme();

  const notify = useNotify();

  const { id } = useParams();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const transfer = searchParams.get("transfer");
  const borrow = searchParams.get("borrow");
  const repay = searchParams.get("repay");

  const [allCoins, setAllCoins] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const getSpotWalletList = async () => {
    try {
      const { data } = await http.get(GetSpotWalletList(true));

      setAllCoins(data?.data);
    } catch (e) {
      notify(e?.response?.data?.message);
    }
  };

  const coin = useMemo(() => {
    const currentCoin = allCoins?.find((c) => c?.coinId === +id);

    return currentCoin;
  }, [id, allCoins]);

  const handleClickOnItems = (page) => {
    if (page.key) {
      setSearchParams({ [page?.key || page]: coin?.coinId, ...page?.extra });
    } else {
      navigate(page);
    }
  };

  const actionItems = [
    {
      value: `/deposit/${coin?.coinId}`,
      text: "Deposit",
    },
    { value: `/withdraw/${coin?.coinId}`, text: "Withdraw" },
    {
      value: {
        key: "transfer",
        extra: { transferSideId: 2 },
      },
      text: "Transfer",
    },
  ];

  const isShowTooltip = useMemo(() => {
    if (!coin) return false;

    return Object.keys(coin).some(
      (key) => key.includes("Comment") && coin[key] != null && coin[key] !== ""
    );
  }, [coin]);


  useEffect(() => {
    getSpotWalletList();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transfer, borrow, repay]);

  return (
    <Container>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <BackButton text="Coin Detail" to="/wallet/spot" />

            <IconButtonStyle onClick={() => setOpenDrawer(true)}>
              <Down color={theme.palette.text.main} />
              <Typography variant="h6">{coin?.coinName}</Typography>
            </IconButtonStyle>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <CoinSingleBox>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <img
                  src={DefaultUrlWeb + coin?.coinImageUrl}
                  alt={coin?.coinName}
                />
              </Grid>

              <Grid item xs={12}>
                <Box display={"flex"} alignItems={"center"} justifyContent={"center"} gap={1}>
                  <Typography>{coin?.coinName}</Typography>

                  {isShowTooltip &&
                    <Box onClick={() => setShowDetails((v) => !v)}>
                      <TooltipIcon color={theme.palette.text.main} />
                    </Box>
                  }
                </Box>
              </Grid>

              <Grid item xs={12}>
                <SpaceBetweenBox>
                  <Typography variant="h6" color="text.secondary">
                    Total
                  </Typography>

                  <Box display={"flex"} gap={1} alignItems={"center"}>
                    {(coin?.totalBalanceComment && showDetails) &&
                      <Typography
                        variant="subtitle1"
                        color="text.alternate"
                        paddingLeft={0.5}
                      >
                        {coin?.totalBalanceComment}
                      </Typography>
                    }

                    <Typography variant="h6">
                      <NumberFormats value={coin?.totalBalance} />
                    </Typography>
                  </Box>
                </SpaceBetweenBox>

                <SpaceBetweenBox>
                  <Typography variant="h6" color="text.secondary">
                    Available
                  </Typography>

                  <Box display={"flex"} gap={1} alignItems={"center"}>
                    {(coin?.availableBalanceComment && showDetails) &&
                      <Typography
                        variant="subtitle1"
                        color="text.alternate"
                        paddingLeft={0.5}
                      >
                        {coin?.availableBalanceComment}
                      </Typography>
                    }
                    <Typography variant="h6">
                      <NumberFormats value={coin?.availableBalance} />
                    </Typography>
                  </Box>
                </SpaceBetweenBox>

                <SpaceBetweenBox>
                  <Typography variant="h6" color="text.secondary">
                    Trade Limit
                  </Typography>
                  <Box display={"flex"} gap={1} alignItems={"center"}>
                    {(coin?.tradeLimitComment && showDetails) &&
                      <Typography
                        variant="subtitle1"
                        color="text.alternate"
                        paddingLeft={0.5}
                      >
                        {coin?.tradeLimitComment}
                      </Typography>
                    }
                    <Typography variant="h6">
                      <NumberFormats value={coin?.tradeLimit} />
                    </Typography>
                  </Box>
                </SpaceBetweenBox>

                <SpaceBetweenBox>
                  <Typography variant="h6" color="text.secondary">
                    Value Blnc (USDT)
                  </Typography>
                  <Typography variant="h6">
                    <NumberFormats value={coin?.totalBalanceUSDT} />
                  </Typography>
                </SpaceBetweenBox>

                <SpaceBetweenBox>
                  <Typography variant="h6" color="text.secondary">
                    Value Blnc (Toman)
                  </Typography>
                  <Typography variant="h6">
                    <NumberFormats value={coin?.totalBalanceToman} />
                  </Typography>
                </SpaceBetweenBox>
              </Grid>
            </Grid>
          </CoinSingleBox>
        </Grid>

        <Grid item xs={12}>
          <ButtonAction items={actionItems} onChange={handleClickOnItems} />
        </Grid>
      </Grid>

      <CoinSingleDrawer
        onClose={() => setOpenDrawer(false)}
        open={openDrawer}
        onCoinClick={(coin) => navigate(`/wallet/spot/${coin?.coinId}`)}
        coinList={allCoins}
      />
    </Container>
  );
}

const CoinSingleBox = styled(Paper)(({ theme }) => ({
  background: theme.palette.background.coinSingle,
  padding: theme.spacing(2),
  textAlign: "center",
  borderRadius: "8px",

  img: {
    width: "90px",
    height: "90px",
    borderRadius: "50%",
  },
}));

const IconButtonStyle = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(1, 1.5),
  backgroundColor: theme.palette.modules.input,
  borderRadius: "10px",
  color: theme.palette.text.main,
  marginRight: theme.spacing(1.25),
  svg: {
    width: "30px",
    paddingRight: theme.spacing(1.5),
  },
}));

export default SpotCoinSingle;
